import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Layout from "../Layout/Layout";
import "./AccessApi.scss";

import { RootState } from "../../redux/rootReducers";
import {
  fetchAccessApiKey,
  deleteAccessApiKey,
  generateAccessApiKey,
  renewAccessApiKeyAction,
  revokeApiKey,
} from "../../redux/actions/accessApi";
import { useLocation, useParams } from "react-router-dom";

// Define a type for the API response
interface ApiKeyResponse {
  id?: string;
  apiKey?: string;
  token?: string;
  accessToken?: string;
  message?: string;
  apiKeyId?: string;
  keyId?: string;
}

const AccessApi: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const userType = useMemo(() => {
    const path = location.pathname.toLowerCase();
    if (path.includes("/admin")) return "admin";
    if (path.includes("/merchant")) return "merchant";
    return "partner";
  }, [location.pathname]);

  const authApi = useSelector((state: RootState) => state.authReducer);
  const { id } = useParams();
  // For admin accessing their own API key, use admin's userId if no ID in params
  const userId = useMemo(() => {
    if (id) return id;
    if (
      authApi?.role?.toLowerCase() === "admin" &&
      location.pathname.toLowerCase().includes("/admin/access")
    ) {
      return authApi.userId || undefined;
    }
    return undefined;
  }, [id, authApi?.role, authApi?.userId, location.pathname]);

  const { token } = authApi;

  const accessApiState = useSelector(
    (state: RootState) => state.accessApiwReducer
  );

  const { loading, error, responseData } = accessApiState;

  const [copied, setCopied] = useState(false);
  const [isOpen, setIsOpen] = useState(true);

  // Safely extract API token
  const apiToken: string = useMemo(() => {
    if (!responseData) return "";

    // If responseData is a string, return it
    if (typeof responseData === "string") {
      return responseData;
    }

    // If responseData is an array, check the first element
    if (Array.isArray(responseData)) {
      const firstItem = responseData[0] as ApiKeyResponse;
      return (
        firstItem?.apiKey ||
        firstItem?.token ||
        firstItem?.accessToken ||
        firstItem?.message ||
        ""
      );
    }

    // If responseData is an object, check its properties
    const data = responseData as ApiKeyResponse;
    return (
      data?.apiKey || data?.token || data?.accessToken || data?.message || ""
    );
  }, [responseData]);

  // Safely extract API key ID
  const apiKeyId: string = useMemo(() => {
    if (!responseData) return "";

    if (typeof responseData === "string") return "";

    if (Array.isArray(responseData)) {
      const firstItem = responseData[0] as ApiKeyResponse;
      return firstItem?.id || firstItem?.apiKeyId || firstItem?.keyId || "";
    }

    const data = responseData as ApiKeyResponse;
    return data?.id || data?.apiKeyId || data?.keyId || "";
  }, [responseData]);

  const maskedToken = apiToken ? "*".repeat(apiToken.length) : "";
  const hasToken = !!apiToken;

  useEffect(() => {
    if (token && userId) {
      dispatch(fetchAccessApiKey(token, userId) as any);
    }
  }, [token, userId, dispatch]);

  const handleGenerate = () => {
    if (!token || !userId) return;
    dispatch(generateAccessApiKey(token, userId, userType) as any);
  };
  const handleRenew = () => {
    if (!token || !userId || !apiToken) return;
    dispatch(renewAccessApiKeyAction(token, userId, userType) as any);
  };

  const handleDisable = () => {
    if (!token || !hasToken) return;

    if (apiKeyId) {
      dispatch(revokeApiKey(token, apiKeyId, userType) as any);
    } else if (userId) {
      dispatch(deleteAccessApiKey(token, userId) as any);
    }
  };

  const handleCopy = async () => {
    if (!apiToken) return;
    try {
      await navigator.clipboard.writeText(apiToken);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy token", err);
    }
  };

  // Determine layout type based on pathname
  const layoutType = useMemo(() => {
    const path = location.pathname.toLowerCase();
    if (path.includes("/admin/access") || path.includes("/admin")) {
      return "admin";
    } else if (path.includes("/partner/") && path.includes("/access")) {
      return "admin"; // Partner uses admin layout type
    } else if (path.includes("/merchant/") && path.includes("/access")) {
      return "merchant";
    }
    // Default based on role
    return authApi?.role?.toLowerCase() === "merchant" ? "merchant" : "admin";
  }, [location.pathname, authApi?.role]);

  return (
    <Layout type={layoutType}>
      <div className="content-side access-api-page">
        <div
          id="payment-Dash-page"
          className="payment-Dash-page"
          style={{ marginTop: "58px" }}
        >
          <div className="access-card">
            <div className="access-card__header">
              <h2 className="access-card__title">Access Token</h2>

              <button
                type="button"
                className="access-card__toggle"
                onClick={() => setIsOpen((prev) => !prev)}
                aria-label={isOpen ? "Collapse" : "Expand"}
              >
                <span
                  className={`chevron ${
                    isOpen ? "chevron--up" : "chevron--down"
                  }`}
                />
              </button>
            </div>

            {isOpen && (
              <div className="access-card__body">
                <div className="access-card__token-row">
                  <input
                    type="text"
                    className="access-card__token-input"
                    readOnly
                    value={maskedToken}
                    placeholder={
                      loading
                        ? "Loading..."
                        : "No token generated yet for this user"
                    }
                  />

                  <button
                    type="button"
                    className="access-card__icon-btn"
                    onClick={handleCopy}
                    disabled={!apiToken || loading}
                    title="Copy token"
                  >
                    📋
                  </button>

                  <button
                    type="button"
                    className="access-card__icon-btn"
                    disabled
                    title="Token settings (coming soon)"
                  >
                    🔗
                  </button>

                  {hasToken && (
                    <button
                      type="button"
                      className="access-card__btn access-card__btn--secondary"
                      onClick={handleDisable}
                      disabled={loading}
                    >
                      Disable
                    </button>
                  )}

                  <button
                    type="button"
                    className="access-card__btn access-card__btn--primary"
                    onClick={hasToken ? handleRenew : handleGenerate}
                    disabled={loading || !userId || !token}
                  >
                    {loading
                      ? "Loading..."
                      : hasToken
                      ? "Renew Token"
                      : "Generate Token"}
                  </button>
                </div>

                {copied && (
                  <div className="access-card__hint">
                    Token copied to clipboard
                  </div>
                )}

                {error && (
                  <div className="access-card__hint access-card__hint--danger">
                    {error}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AccessApi;
